/**
 * ConsultationRequestModal — form for real (non-demo) professionals.
 *
 * Fires POST /api/marketplace/consultation-requests. Handles the
 * following backend responses:
 *   - 201                                              → success toast, closes after ~3s
 *   - 422 consultation_content_failed_guardrail        → inline rephrase message (no phrase leak)
 *   - 422 demo_profile_consultation_unavailable        → defensive fallback (should never happen)
 *   - 429 consultation_rate_limited                    → daily-limit copy
 *   - 5xx                                              → retryable inline error + Retry button
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/api/client";

const TOPIC_MAX = 200;
const CONTEXT_MAX = 1000;

export const CONSULTATION_COPY = {
  titleReal: "Request a consultation",
  descriptionReal:
    "Describe what you need help with. Our admin team reviews every request " +
    "and forwards it to the professional. This is not an automatic booking.",
  successHeadline: "Consultation request submitted.",
  successSublinePrefix: "The admin team will forward your request to ",
  guardrailInline:
    "This content contains language we can't process. Please rephrase and try again.",
  rateLimit:
    "You've reached the daily limit for consultation requests. Please try again tomorrow.",
  demoFallback: "Consultation requests aren't available for this profile.",
  retryHint: "Something went wrong. Please try again.",
};

export default function ConsultationRequestModal({
  open,
  onOpenChange,
  professional,
}) {
  const [topic, setTopic] = useState("");
  const [context, setContext] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTopic("");
      setContext("");
      setPreferredTime("");
      setSubmitting(false);
      setError(null);
      setErrorCode(null);
      setSuccessMsg(null);
    }
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [open]);

  const canSubmit = useMemo(
    () =>
      !submitting &&
      topic.trim().length > 0 &&
      topic.length <= TOPIC_MAX &&
      context.trim().length > 0 &&
      context.length <= CONTEXT_MAX,
    [submitting, topic, context]
  );

  async function submit() {
    if (!professional?.id) return;
    setSubmitting(true);
    setError(null);
    setErrorCode(null);
    try {
      await api.post("/marketplace/consultation-requests", {
        professional_id: professional.id,
        topic: topic.trim(),
        context: context.trim(),
        preferred_contact_time: preferredTime.trim() || null,
      });
      setSuccessMsg(
        CONSULTATION_COPY.successSublinePrefix + (professional?.name || "the professional") + "."
      );
      // Close after ~3s but do NOT auto-navigate.
      closeTimerRef.current = setTimeout(() => {
        onOpenChange?.(false);
      }, 3000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (status === 422 && code === "consultation_content_failed_guardrail") {
        setError(CONSULTATION_COPY.guardrailInline);
        setErrorCode(code);
      } else if (status === 422 && code === "demo_profile_consultation_unavailable") {
        // Defensive fallback — should never be reachable because the
        // button is hidden on demo profiles.
        setError(CONSULTATION_COPY.demoFallback);
        setErrorCode(code);
        closeTimerRef.current = setTimeout(() => {
          onOpenChange?.(false);
        }, 2500);
      } else if (status === 429) {
        setError(CONSULTATION_COPY.rateLimit);
        setErrorCode("consultation_rate_limited");
      } else if (status >= 500) {
        setError(formatApiError(err) || CONSULTATION_COPY.retryHint);
        setErrorCode("retryable");
      } else {
        setError(formatApiError(err) || CONSULTATION_COPY.retryHint);
        setErrorCode("generic");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="consultation-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{CONSULTATION_COPY.titleReal}</DialogTitle>
          <DialogDescription>{CONSULTATION_COPY.descriptionReal}</DialogDescription>
        </DialogHeader>

        {successMsg ? (
          <div
            data-testid="consultation-success"
            className="rounded-md p-4 text-sm"
            style={{
              background: "rgba(52,211,153,0.12)",
              border: "1px solid rgba(52,211,153,0.35)",
              color: "var(--v2-text, #F5F5F0)",
            }}
          >
            <div className="font-semibold mb-1">{CONSULTATION_COPY.successHeadline}</div>
            <div>{successMsg}</div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) submit();
            }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="cons-topic" className="text-sm font-medium block mb-1">
                What do you need help with? <span className="text-red-500">*</span>
              </label>
              <input
                id="cons-topic"
                data-testid="consultation-topic-input"
                type="text"
                value={topic}
                maxLength={TOPIC_MAX}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="E.g. Reviewing my grant budget narrative"
                className="w-full rounded-md border px-3 py-2 text-sm bg-transparent"
                style={{ borderColor: "var(--v2-border, rgba(255,255,255,0.18))" }}
                required
              />
              <div
                data-testid="consultation-topic-counter"
                className="mt-1 text-[11px] text-right"
                style={{ color: "var(--v2-text-muted, #A1A1AA)" }}
              >
                {topic.length} / {TOPIC_MAX}
              </div>
            </div>

            <div>
              <label htmlFor="cons-context" className="text-sm font-medium block mb-1">
                Context <span className="text-red-500">*</span>
              </label>
              <textarea
                id="cons-context"
                data-testid="consultation-context-input"
                value={context}
                maxLength={CONTEXT_MAX}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Share the background so the professional can give you specific guidance."
                rows={5}
                className="w-full rounded-md border px-3 py-2 text-sm bg-transparent resize-y"
                style={{ borderColor: "var(--v2-border, rgba(255,255,255,0.18))" }}
                required
              />
              <div
                data-testid="consultation-context-counter"
                className="mt-1 text-[11px] text-right"
                style={{ color: "var(--v2-text-muted, #A1A1AA)" }}
              >
                {context.length} / {CONTEXT_MAX}
              </div>
            </div>

            <div>
              <label htmlFor="cons-time" className="text-sm font-medium block mb-1">
                Preferred contact time <span className="text-xs text-[var(--v2-text-muted,#A1A1AA)] font-normal">(optional)</span>
              </label>
              <input
                id="cons-time"
                data-testid="consultation-preferred-time-input"
                type="text"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                placeholder="E.g. Weekday mornings, US Pacific"
                className="w-full rounded-md border px-3 py-2 text-sm bg-transparent"
                style={{ borderColor: "var(--v2-border, rgba(255,255,255,0.18))" }}
              />
            </div>

            {error && (
              <div
                data-testid="consultation-error"
                data-error-code={errorCode || ""}
                role="alert"
                className="rounded-md p-3 text-sm flex items-start justify-between gap-3"
                style={{
                  background: "rgba(239,68,68,0.10)",
                  border: "1px solid rgba(239,68,68,0.34)",
                  color: "var(--v2-text, #F5F5F0)",
                }}
              >
                <span>{error}</span>
                {errorCode === "retryable" && (
                  <button
                    type="button"
                    data-testid="consultation-retry-btn"
                    onClick={submit}
                    className="text-xs font-semibold underline"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}

            <DialogFooter className="pt-2">
              <button
                type="button"
                onClick={() => onOpenChange?.(false)}
                className="px-4 py-2 rounded-md text-sm font-medium border"
                style={{ borderColor: "var(--v2-border, rgba(255,255,255,0.18))" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="consultation-submit"
                disabled={!canSubmit}
                className="px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                style={{ background: "var(--v2-accent, #5D50C8)", color: "var(--v2-accent-ink, #FFFFFF)" }}
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
